import React, { useEffect, useState } from 'react'
import { graphql } from 'react-apollo'

import { usePlaidLinkingHandlers } from '@acorns/web-bank-linking'
import { path } from 'ramda'
import {
  branch,
  compose,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import { PlaidLinkProps, withPlaidLinkProps } from 'components/HOCs/WithPlaidLinkProps'
import { ActionType, PageName } from 'generated/types'
import { PLAID_CONFIG, PLAID_LINK_LIBRARY_SOURCE_URI } from 'pages/Institutions/constants'
import { processOnPlaidExitError } from 'pages/Institutions/utils'
import Environment, { withEnv } from 'utils/environment'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { withSetPrimaryFundingSource } from 'utils/mutators/bank-linking/bankLinking'
import { withPrompt } from 'utils/prompts'

import plaidLinkWebhookQuery from './gql/plaid-link-webhook.query.gql'

declare global {
  interface Window {
    Plaid: any
  }
}

type Props = {
  env: Environment
  submitting: boolean
  loading: boolean
  plaidWebhook: string
}

type HandlerProps = {
  onPlaidLinkExit: (error: any, metadata: any) => void
}

type InnerProps = Props & HandlerProps & PlaidLinkProps & WithFlowRouterProps

export const enhance = compose<InnerProps, {}>(
  withState('submitting', 'setSubmitting', false),
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isDynamicFunnelEnabled, routeToPage }) => ({ isDynamicFunnelEnabled, routeToPage }),
  ),
  // Note: We only need to fetch this once
  graphql(plaidLinkWebhookQuery, {
    props: ({ data }) => ({
      plaidWebhook: path(['linkedAccountWebhooks', 'plaid'], data),
    }),
  }),
  withEnv,
  withFlowRouter,
  withSetPrimaryFundingSource,
  withPrompt,
  withProps(() => {
    return {
      isRedirect: true,
    }
  }),
  withPlaidLinkProps,
  withHandlers({
    onPlaidLinkExit: ({ setSubmitting, route, isDynamicFunnelEnabled, routeToPage }) => error => {
      if (error) {
        processOnPlaidExitError(error)
      }
      setSubmitting(false)

      isDynamicFunnelEnabled
        ? routeToPage(PageName.PlaidLinkRedirect, ActionType.Link)
        : route({ from: FlowRoute.PlaidLinkRedirect, action: 'link-account' })
    },
  }),
  branch(({ loading, submitting }) => loading || submitting, renderComponent(CenteredSpinner)),
)

const PlaidLinkRedirect: React.FC<InnerProps> = ({
  flow,
  handleAvailableFundingSource,
  handleLinkAccountError,
  onPlaidLinkEvent,
  onPlaidLinkExit,
  onPlaidLinkSuccess: onSuccess,
  plaidWebhook,
  env,
}) => {
  const [plaidLoaded, setPlaidLoaded] = useState(false)

  const loadPlaidScript = () => {
    const script = document.createElement('script')
    script.src = PLAID_LINK_LIBRARY_SOURCE_URI
    script.async = true
    script.onload = () => {
      setPlaidLoaded(true)
    }
    document.body.appendChild(script)
  }

  const { onPlaidLinkSuccess } = usePlaidLinkingHandlers({
    disableRoundUps: flow === 'later' || flow === 'spend',
    onAvailableFundingSources: handleAvailableFundingSource,
    onLinkAccountError: handleLinkAccountError,
    onSuccess,
  })

  useEffect(() => {
    loadPlaidScript()
  }, [])

  useEffect(() => {
    if (plaidLoaded && window.Plaid) {
      const linkToken = localStorage.getItem('plaid_link_token')

      const config = {
        ...PLAID_CONFIG,
        webhook: plaidWebhook,
        token: linkToken,
        receivedRedirectUri: window.location.href,
        env: env.get('plaidEnv'),
        key: env.get('plaidLinkKey'),
        onExit: (error: any, metadata: any) => {
          onPlaidLinkExit(error, metadata)
          plaidHandler.destroy()
        },
        onSuccess: onPlaidLinkSuccess,
        onEvent: onPlaidLinkEvent,
      }

      const plaidHandler = window.Plaid.create(config)

      plaidHandler.open()
    }
  }, [plaidLoaded])

  return <></>
}

export { PlaidLinkRedirect }
export default enhance(PlaidLinkRedirect)
